import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi, formatApiError } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import { X, ExternalLink } from "lucide-react";
import OrangeFixProofCard from "@/shared/OrangeFixProofCard";

// Phase 3E-A — Global Orange HQ Fix queue.
// Reads /api/hq/control/fix-queue (canonical). No local state duplication —
// every action re-fetches so the view is always the ledger's truth.

const STATUS_TABS = [
  { key: "",           label: "All active" },
  { key: "REQUESTED",  label: "Requested" },
  { key: "REVIEWING",  label: "Reviewing" },
  { key: "CANCELLED",  label: "Cancelled" },
];

const STATUS_TONE = {
  REQUESTED:  { bg: "#FFF7EE", fg: "#C1350B", bd: "#FCE1C7" },
  REVIEWING:  { bg: "#F0F7FF", fg: "#2E7CF6", bd: "#D6E7FF" },
  CANCELLED:  { bg: "#F2F2F2", fg: "#4A4A4A", bd: "#EAEAEA" },
};
const COMPLEXITY_TONE = {
  LOW:    { bg: "#EBFBF2", fg: "#0FA958" },
  MEDIUM: { bg: "#FFF9E6", fg: "#8A6D00" },
  HIGH:   { bg: "#FFE9DA", fg: "#C1350B" },
};

function StatusPill({ value, testId }) {
  const c = STATUS_TONE[value] || STATUS_TONE.CANCELLED;
  return (
    <span data-testid={testId}
          className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black tracking-[0.16em] uppercase"
          style={{ background: c.bg, color: c.fg, border: `1px solid ${c.bd}` }}>
      {value || "—"}
    </span>
  );
}

function ComplexityPill({ value }) {
  const c = COMPLEXITY_TONE[value] || COMPLEXITY_TONE.MEDIUM;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-black tracking-[0.16em] uppercase"
          style={{ background: c.bg, color: c.fg }}>
      {value || "MEDIUM"}
    </span>
  );
}

export default function HQOptimiseFix() {
  const [status, setStatus] = useState("");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setData(null);
    const params = {};
    if (status) params.status = status;
    return hqApi.get("/control/fix-queue", { params })
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatApiError(e?.response?.data?.detail)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="hq-optimise-fix">
      <Card className="p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-[#F6F5F2] p-1" data-testid="hq-fix-status-filter">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key || "all"}
                type="button"
                onClick={() => setStatus(t.key)}
                data-testid={`hq-fix-status-${t.key || "all"}`}
                className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase transition-colors ${
                  status === t.key ? "bg-black text-white" : "text-black/60 hover:text-black"
                }`}
              >{t.label}</button>
            ))}
          </div>
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]" data-testid="hq-fix-total">
            {data ? `${data.total} fix job${data.total === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {err ? (
          <div className="p-6 text-sm text-[#C1350B]" data-testid="hq-fix-error">{err}</div>
        ) : !data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No fix jobs yet"
            hint="Fix jobs are created from a client's Orange Fix tab against an open or regressed issue."
            testId="hq-fix-empty"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Issue</th>
                  <th className="text-left px-5 py-3 font-bold">Category</th>
                  <th className="text-left px-5 py-3 font-bold">Complexity</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Requested</th>
                  <th className="text-left px-5 py-3 font-bold">Client</th>
                  <th className="text-left px-5 py-3 font-bold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((j) => (
                  <tr key={j.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]"
                      data-testid={`hq-fix-row-${j.id}`}>
                    <td className="px-5 py-3 font-bold break-words max-w-md">
                      <button type="button"
                              onClick={() => setSelected(j)}
                              data-testid={`hq-fix-open-${j.id}`}
                              className="text-left hover:text-[#FF6A00]">
                        {j.title_at_request || j.issue_key || "—"}
                      </button>
                      {j.code && (
                        <div className="mt-0.5 text-[10px] tracking-[0.14em] uppercase text-[#8A8A8A]">
                          code · {j.code}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[13px] text-[#4A4A4A]">{j.category || "—"}</td>
                    <td className="px-5 py-3"><ComplexityPill value={j.guidance?.complexity}/></td>
                    <td className="px-5 py-3"><StatusPill value={j.status}/></td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">
                      {j.requested_at ? new Date(j.requested_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-5 py-3 text-[13px]">
                      <Link to={`/hq/clients/${j.business_id}`}
                            data-testid={`hq-fix-client-${j.id}`}
                            className="text-[#FF6A00] hover:underline inline-flex items-center gap-1">
                        {(j.business_id || "").slice(0, 8) || "—"} <ExternalLink size={11}/>
                      </Link>
                    </td>
                    <td className="px-5 py-3">
                      <button type="button"
                              onClick={() => setSelected(j)}
                              data-testid={`hq-fix-view-${j.id}`}
                              className="px-3 py-1.5 rounded-full border border-[#EAEAEA] bg-white text-[10px] font-black tracking-[0.2em] uppercase hover:border-[#FF6A00]">
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected && (
        <FixJobDrawer
          job={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { load(); setSelected(null); }}
        />
      )}
    </div>
  );
}

export function FixJobDrawer({ job, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(job);
  const [notes, setNotes] = useState(job?.internal_notes || "");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteAmt, setQuoteAmt] = useState("");
  const [quoteDesc, setQuoteDesc] = useState("");
  const [quoteCur, setQuoteCur] = useState("GBP");
  const [proof, setProof] = useState(null);
  const [proofOpen, setProofOpen] = useState(false);

  useEffect(() => {
    setDetail(job);
    setNotes(job?.internal_notes || "");
    setProof(null);
    setProofOpen(false);
  }, [job?.id]);

  // Auto-load proof for verified jobs so the "View verified result" button is
  // meaningful the moment the drawer opens.
  useEffect(() => {
    if (detail?.status === "VERIFIED_RESOLVED" && detail?.id && !proof) {
      hqApi.get(`/control/fix-requests/${detail.id}/proof`)
        .then((r) => setProof(r.data))
        .catch(() => setProof({ available: false }));
    }
  }, [detail?.status, detail?.id, proof]);

  const active = useMemo(() =>
    ["REQUESTED", "REVIEWING", "QUOTED", "AWAITING_PAYMENT", "PAID",
     "IN_PROGRESS", "WORK_COMPLETED", "VERIFYING"].includes(detail?.status),
  [detail?.status]);

  if (!job) return null;
  const g = detail?.guidance || {};
  const s = detail?.status;

  const call = async (fn, ok) => {
    setBusy(true);
    try {
      const r = await fn();
      setDetail(r.data);
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const setAccess = (type, state) =>
    call(() => hqApi.patch(`/control/fix-requests/${job.id}/access`,
                            { access_type: type, state }),
         "Access updated");

  const review     = () => call(() => hqApi.post(`/control/fix-requests/${job.id}/review`), "Marked reviewing");
  const startWork  = () => call(() => hqApi.post(`/control/fix-requests/${job.id}/start-work`), "Work started");
  const complete   = () => call(() => hqApi.post(`/control/fix-requests/${job.id}/complete-work`), "Work completed");
  const verify     = () => call(() => hqApi.post(`/control/fix-requests/${job.id}/verify`), "Verification triggered");
  const returnWork = () => call(() => hqApi.post(`/control/fix-requests/${job.id}/return-to-work`), "Returned to work");
  const sendQ      = () => call(() => hqApi.post(`/control/fix-requests/${job.id}/send-quote`), "Quote sent");

  const saveNotes = async () => {
    setBusy(true);
    try {
      const r = await hqApi.patch(`/control/fix-requests/${job.id}/notes`,
        { internal_notes: notes });
      setDetail(r.data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitQuote = async () => {
    const amt = parseInt(String(quoteAmt).replace(/\D/g, ""), 10);
    if (!amt || amt <= 0) { toast.error("Enter an amount in minor units"); return; }
    if (!quoteDesc.trim()) { toast.error("Enter a description"); return; }
    setBusy(true);
    try {
      const r = await hqApi.post(`/control/fix-requests/${job.id}/quote`,
        { description: quoteDesc, amount_minor: amt, currency: quoteCur });
      setDetail(r.data);
      setQuoteOpen(false);
      toast.success("Quote created");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!confirm("Cancel this fix job? Historical record will remain.")) return;
    setBusy(true);
    try {
      await hqApi.post(`/control/fix-requests/${job.id}/cancel`);
      toast.success("Fix job cancelled");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="hq-fix-drawer">
      <button aria-label="Close" onClick={onClose} className="flex-1 bg-black/40" />
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Orange Fix workbench</div>
            <h2 className="mt-1 font-black text-xl md:text-2xl tracking-[-0.01em]"
                data-testid="hq-fix-drawer-title">
              {detail.title_at_request || detail.issue_key || "—"}
            </h2>
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <StatusPill value={detail.status} testId="hq-fix-drawer-status"/>
              <ComplexityPill value={g.complexity}/>
              <span className="text-[10px] tracking-[0.16em] uppercase text-[#8A8A8A]">
                {detail.category || "—"}{detail.code ? ` · code ${detail.code}` : ""}
              </span>
            </div>
          </div>
          <button onClick={onClose} data-testid="hq-fix-drawer-close"
                  className="p-2 rounded-full hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>

        {/* Lifecycle action bar */}
        <div className="mt-5 flex flex-wrap gap-2 border-t border-b border-[#EAEAEA] py-3"
             data-testid="hq-fix-actions">
          {s === "REQUESTED" && <ActionBtn onClick={review} busy={busy} testId="hq-fix-action-review">Mark reviewing</ActionBtn>}
          {(s === "REQUESTED" || s === "REVIEWING") && <ActionBtn primary onClick={() => setQuoteOpen(true)} busy={busy} testId="hq-fix-action-quote">Create quote</ActionBtn>}
          {s === "QUOTED" && <ActionBtn primary onClick={sendQ} busy={busy} testId="hq-fix-action-send-quote">Send quote email</ActionBtn>}
          {s === "AWAITING_PAYMENT" && (
            <span className="text-[11px] tracking-[0.16em] uppercase text-[#8A8A8A] px-3 py-2 rounded-full bg-[#FFF9E6] border border-[#F5E4B0]">
              Awaiting webhook confirmation — do NOT mark paid manually
            </span>
          )}
          {s === "PAID" && <ActionBtn primary onClick={startWork} busy={busy} testId="hq-fix-action-start">Start work</ActionBtn>}
          {s === "IN_PROGRESS" && <ActionBtn primary onClick={complete} busy={busy} testId="hq-fix-action-complete">Mark work completed</ActionBtn>}
          {s === "WORK_COMPLETED" && <ActionBtn primary onClick={verify} busy={busy} testId="hq-fix-action-verify">Verify with Orange Optimise</ActionBtn>}
          {s === "STILL_DETECTED" && <>
            <ActionBtn primary onClick={returnWork} busy={busy} testId="hq-fix-action-return">Return to work</ActionBtn>
            <ActionBtn onClick={verify} busy={busy} testId="hq-fix-action-reverify">Re-verify</ActionBtn>
          </>}
          {s === "VERIFYING" && (
            <span className="text-[11px] tracking-[0.16em] uppercase text-[#2E7CF6] px-3 py-2 rounded-full bg-[#F0F7FF] border border-[#D6E7FF]">
              Verifying — running a real Orange Optimise rescan
            </span>
          )}
          {s === "VERIFIED_RESOLVED" && (
            <>
              <span data-testid="hq-fix-verified-badge"
                    className="text-[11px] tracking-[0.16em] uppercase text-[#0FA958] px-3 py-2 rounded-full bg-[#EBFBF2] border border-[#B7EBCF] font-black">
                Verified ✓ — Optimise confirmed resolved
              </span>
              <ActionBtn primary onClick={() => setProofOpen((v) => !v)} busy={busy}
                         testId="hq-fix-view-proof">
                {proofOpen ? "Hide verified result" : "View verified result"}
              </ActionBtn>
            </>
          )}
        </div>

        {/* Phase 3F — Verified proof card */}
        {s === "VERIFIED_RESOLVED" && proofOpen && (
          <div className="mt-6" data-testid="hq-fix-proof-wrapper">
            {!proof ? (
              <div className="p-4 text-sm text-[#8A8A8A]">Loading result…</div>
            ) : proof.available ? (
              <OrangeFixProofCard proof={proof}/>
            ) : (
              <div className="p-4 rounded-xl bg-[#F6F5F2] text-sm text-[#4A4A4A]"
                   data-testid="hq-fix-proof-unavailable">
                Verified proof not available yet ({proof.reason || "reason unknown"}).
              </div>
            )}
          </div>
        )}

        {/* Quote inputs */}
        {quoteOpen && (
          <div className="mt-4 p-4 rounded-2xl border-2 border-[#0A0A0A] bg-[#FFFDF6]" data-testid="hq-fix-quote-form">
            <Label>Create quote</Label>
            <p className="mt-1 text-[12px] text-[#4A4A4A]">The amount you enter creates a canonical invoice. Payment provider is chosen automatically by currency (AED→Stripe · GBP/USD/EUR→PayPal).</p>
            <textarea
              value={quoteDesc}
              onChange={(e) => setQuoteDesc(e.target.value)}
              placeholder="Customer-facing description (what the fix covers)"
              data-testid="hq-fix-quote-desc"
              className="mt-3 w-full border border-[#EAEAEA] rounded-xl p-3 text-sm min-h-[70px]"
            />
            <div className="mt-2 flex gap-2 items-end">
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">Amount (minor units)</div>
                <input value={quoteAmt} onChange={(e) => setQuoteAmt(e.target.value)}
                       placeholder="e.g. 9500 = 95.00"
                       data-testid="hq-fix-quote-amount"
                       className="mt-1 w-full border border-[#EAEAEA] rounded-full px-3 py-2 text-sm" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#8A8A8A]">Currency</div>
                <select value={quoteCur} onChange={(e) => setQuoteCur(e.target.value)}
                        data-testid="hq-fix-quote-currency"
                        className="mt-1 border border-[#EAEAEA] rounded-full px-3 py-2 text-sm font-black">
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="AED">AED</option>
                </select>
              </div>
              <ActionBtn primary onClick={submitQuote} busy={busy} testId="hq-fix-quote-submit">Create</ActionBtn>
              <ActionBtn onClick={() => setQuoteOpen(false)} busy={busy} testId="hq-fix-quote-cancel">Cancel</ActionBtn>
            </div>
          </div>
        )}

        <section className="mt-6 space-y-4">
          <Block label="What it means"    value={g.what_it_means}    testId="hq-fix-what"/>
          <Block label="Why it matters"   value={g.why_it_matters}   testId="hq-fix-why"/>
          <Block label="Suggested fix"    value={g.suggested_fix}    testId="hq-fix-how"/>
          <div>
            <Label>Likely access</Label>
            <div className="mt-1 flex flex-wrap gap-1.5" data-testid="hq-fix-access-list">
              {(g.likely_access || []).length ? g.likely_access.map((a) => (
                <span key={a} className="inline-flex px-2 py-0.5 rounded-full bg-[#F6F5F2] text-[10px] tracking-[0.14em] uppercase font-bold">
                  {a}
                </span>
              )) : <span className="text-[13px] text-[#8A8A8A]">Manual technical review required.</span>}
            </div>
          </div>
          <div>
            <Label>Capability</Label>
            <div className="mt-1 text-[13px]" data-testid="hq-fix-capability">{g.capability || "SPECIALIST MAY BE REQUIRED"}</div>
          </div>
        </section>

        <section className="mt-8">
          <Label>Access checklist</Label>
          <div className="mt-2 space-y-2" data-testid="hq-fix-access-checklist">
            {Object.entries(detail.access_checklist || {}).map(([type, state]) => (
              <div key={type} className="flex items-center justify-between gap-3 border border-[#EAEAEA] rounded-xl px-3 py-2">
                <div className="text-[11px] tracking-[0.16em] uppercase font-bold">{type}</div>
                <select
                  value={state}
                  disabled={busy || !active}
                  onChange={(e) => setAccess(type, e.target.value)}
                  data-testid={`hq-fix-access-${type}`}
                  className="text-[11px] font-black tracking-[0.14em] uppercase bg-white border border-[#EAEAEA] rounded-full px-2.5 py-1"
                >
                  <option value="NOT_REQUIRED">NOT_REQUIRED</option>
                  <option value="REQUIRED">REQUIRED</option>
                  <option value="RECEIVED">RECEIVED</option>
                </select>
              </div>
            ))}
          </div>
        </section>

        {/* Internal notes — HQ only */}
        <section className="mt-8">
          <Label>Internal work notes (HQ only)</Label>
          <p className="mt-1 text-[11px] text-[#C1350B] font-bold tracking-[0.06em]">
            Never store customer passwords, API keys or private credentials here.
          </p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={busy || !active}
            data-testid="hq-fix-notes"
            placeholder="e.g. CF Transform Rule applied. Verified header on curl."
            className="mt-2 w-full border border-[#EAEAEA] rounded-xl p-3 text-sm min-h-[90px]"
          />
          {active && (
            <div className="mt-2 flex justify-end">
              <ActionBtn onClick={saveNotes} busy={busy} testId="hq-fix-notes-save">Save notes</ActionBtn>
            </div>
          )}
        </section>

        {/* Verification result panel */}
        {(detail.verification_result || detail.verification_scan_id) && (
          <section className="mt-6 p-4 rounded-xl border border-[#EAEAEA] bg-[#F6F5F2]"
                   data-testid="hq-fix-verification-panel">
            <Label>Last verification</Label>
            <div className="mt-1 text-[13px]">
              Result: <strong>{detail.verification_result || "—"}</strong>
              {detail.verified_at && <> · {new Date(detail.verified_at).toLocaleString()}</>}
              {detail.verification_scan_id && <> · scan {detail.verification_scan_id.slice(0,8)}</>}
            </div>
            {detail.verification_last_error && (
              <div className="mt-1 text-[12px] text-[#C1350B]">
                {detail.verification_last_error}
              </div>
            )}
          </section>
        )}

        {active && (
          <div className="mt-8 pt-6 border-t border-[#EAEAEA] flex justify-end">
            <button
              type="button"
              onClick={cancel}
              disabled={busy}
              data-testid="hq-fix-cancel-btn"
              className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[10px] font-black tracking-[0.18em] uppercase hover:border-[#C1350B] hover:text-[#C1350B] disabled:opacity-50"
            >
              Cancel fix job
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionBtn({ children, onClick, busy, testId, primary }) {
  return (
    <button type="button" onClick={onClick} disabled={busy} data-testid={testId}
      className={`px-3 py-2 rounded-full text-[10px] font-black tracking-[0.16em] uppercase transition-colors disabled:opacity-50 ${
        primary
          ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]"
          : "bg-white border border-[#EAEAEA] hover:border-[#FF6A00]"
      }`}>
      {children}
    </button>
  );
}

function Label({ children }) {
  return <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{children}</div>;
}
function Block({ label, value, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <p className="mt-1 text-[14px] leading-[1.55] text-[#0A0A0A]" data-testid={testId}>
        {value || "Manual technical review required."}
      </p>
    </div>
  );
}
